import { useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCcw, Search, ArrowLeftRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { api } from "@/store/useStore";
import { formatCurrency } from "@/lib/utils";

export default function ReturnsDialog({ open, onOpenChange, onDone }) {
  const [receipt, setReceipt] = useState("");
  const [sale, setSale] = useState(null);
  const [looking, setLooking] = useState(false);
  const [quantities, setQuantities] = useState({});
  const [mode, setMode] = useState("refund");
  const [refundMethod, setRefundMethod] = useState("cash");
  const [reason, setReason] = useState("Wrong size");
  const [swapVariantId, setSwapVariantId] = useState("");
  const [swapOptions, setSwapOptions] = useState([]);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setSale(null); setQuantities({}); setReceipt("");
    setSwapVariantId(""); setSwapOptions([]); setMode("refund");
  };

  const lookup = async () => {
    if (!receipt.trim()) return;
    setLooking(true);
    try {
      const data = await api.get(`/returns/lookup?receipt=${encodeURIComponent(receipt.trim())}`);
      setSale(data);
      const next = {};
      data.lines.forEach((line) => { next[line.line_index] = 0; });
      setQuantities(next);
    } catch (error) {
      toast.error(error.message || "Sale not found");
      setSale(null);
    } finally {
      setLooking(false);
    }
  };

  const selectedLines = (sale?.lines || []).filter((line) => (quantities[line.line_index] || 0) > 0);
  const returnValue = selectedLines.reduce(
    (sum, line) => sum + line.price * (quantities[line.line_index] || 0), 0
  );

  const loadSwapOptions = async (line) => {
    try {
      const catalog = await api.get("/pos/catalog");
      const product = (catalog.products || []).find((p) => p.id === line.product_id);
      setSwapOptions(
        (product?.variants || []).filter((v) => v.id !== line.variant_id && v.stock > 0)
      );
    } catch (error) {
      toast.error("Could not load the other sizes");
    }
  };

  const setQty = async (line, value) => {
    const qty = Math.max(0, Math.min(line.quantity_available, Number(value) || 0));
    setQuantities((prev) => ({ ...prev, [line.line_index]: qty }));
    if (qty > 0 && line.variant_id && mode === "exchange") await loadSwapOptions(line);
  };

  const submit = async () => {
    if (selectedLines.length === 0) { toast.error("Set how many to return"); return; }
    if (mode === "exchange") {
      if (selectedLines.length > 1) { toast.error("Exchange one line at a time"); return; }
      if (!swapVariantId) { toast.error("Choose the replacement size"); return; }
    }
    setSaving(true);
    try {
      const swap = swapOptions.find((v) => v.id === swapVariantId);
      const line = selectedLines[0];
      const payload = {
        order_id: sale.order_id,
        client_reference: `${sale.order_id}:${Date.now()}`,
        mode,
        refund_method: refundMethod,
        reason,
        lines: selectedLines.map((l) => ({
          product_id: l.product_id,
          variant_id: l.variant_id,
          name: l.name,
          variant_label: l.variant_label,
          price: l.price,
          quantity: quantities[l.line_index],
        })),
        exchange_lines: mode === "exchange" && swap ? [{
          product_id: line.product_id,
          variant_id: swap.id,
          name: line.name,
          variant_label: swap.label,
          price: swap.price,
          quantity: quantities[line.line_index],
        }] : [],
      };
      const result = await api.post("/returns", payload);
      toast.success(result.message);
      onDone?.(result);
      reset();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.message || "Could not complete the return");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) reset(); onOpenChange(next); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="returns-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5" /> Returns & exchanges
          </DialogTitle>
          <DialogDescription>
            Scan or type the receipt number, then choose what is coming back.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          <Input
            autoFocus
            value={receipt}
            onChange={(e) => setReceipt(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && lookup()}
            placeholder="Receipt / order number"
            className="h-11"
            data-testid="returns-receipt-input"
          />
          <Button onClick={lookup} disabled={looking} className="h-11" data-testid="returns-lookup-btn">
            {looking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            <span className="ml-2">Find</span>
          </Button>
        </div>

        {sale && (
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground" data-testid="returns-sale-summary">
              {sale.receipt} · {(sale.sold_at || "").slice(0, 10)} · {formatCurrency(sale.total)} ·{" "}
              {sale.customer_name || "Walk-in"}
            </div>

            <ScrollArea className="max-h-64 border rounded-md">
              <div className="divide-y">
                {sale.lines.map((line) => (
                  <div key={line.line_index} className="flex items-center gap-3 p-3" data-testid={`returns-line-${line.line_index}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {line.name}{line.variant_label ? ` · ${line.variant_label}` : ""}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatCurrency(line.price)} · sold {line.quantity_sold}
                        {line.quantity_returned > 0 && ` · already returned ${line.quantity_returned}`}
                      </p>
                    </div>
                    <Input
                      type="number"
                      min="0"
                      max={line.quantity_available}
                      className="w-20 h-9"
                      value={quantities[line.line_index] ?? 0}
                      onChange={(e) => setQty(line, e.target.value)}
                      disabled={line.quantity_available <= 0}
                      data-testid={`returns-qty-${line.line_index}`}
                    />
                  </div>
                ))}
              </div>
            </ScrollArea>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">What are we doing</Label>
                <Select value={mode} onValueChange={(value) => { setMode(value); setSwapVariantId(""); }}>
                  <SelectTrigger data-testid="returns-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="refund">Refund</SelectItem>
                    <SelectItem value="exchange">Exchange for another size</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {mode === "refund" ? (
                <div>
                  <Label className="text-xs text-muted-foreground">Refund by</Label>
                  <Select value={refundMethod} onValueChange={setRefundMethod}>
                    <SelectTrigger data-testid="returns-refund-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="original">Original payment method</SelectItem>
                      <SelectItem value="store_credit">Store credit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div>
                  <Label className="text-xs text-muted-foreground">Replacement</Label>
                  <Select value={swapVariantId} onValueChange={setSwapVariantId}>
                    <SelectTrigger data-testid="returns-swap-select">
                      <SelectValue placeholder={swapOptions.length ? "Choose a size" : "Set a quantity first"} />
                    </SelectTrigger>
                    <SelectContent>
                      {swapOptions.map((v) => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.label} — {v.stock} in stock
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">Reason</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="returns-reason-input" />
            </div>

            <div className="rounded-md bg-muted/40 p-3 text-sm" data-testid="returns-summary">
              Returning {selectedLines.reduce((n, l) => n + quantities[l.line_index], 0)} item(s) worth{" "}
              <strong>{formatCurrency(returnValue)}</strong>
              {mode === "exchange" && swapVariantId && (() => {
                const swap = swapOptions.find((v) => v.id === swapVariantId);
                const line = selectedLines[0];
                const diff = (swap?.price || 0) * (quantities[line?.line_index] || 0) - returnValue;
                return (
                  <span>
                    {" · "}
                    {diff > 0 ? `collect ${formatCurrency(diff)}` : diff < 0 ? `refund ${formatCurrency(Math.abs(diff))}` : "no money due"}
                  </span>
                );
              })()}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }}>Cancel</Button>
          <Button onClick={submit} disabled={!sale || saving || selectedLines.length === 0} data-testid="returns-submit-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowLeftRight className="h-4 w-4 mr-2" />}
            {mode === "refund" ? "Refund" : "Exchange"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
